/*
Copyright (C) 2020  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { Bot } from "lucide-react";

import { ProviderBadge } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import IconTile from "@src/components/ui/IconTile";
import {
  Cell,
  FlexCell,
  ListRow,
  Muted,
  Num,
  Pill,
  Primary,
  Secondary,
  Stack,
  StatePill,
  T9,
  formatDateTime,
  formatShortDateTime,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

import type { MinionPool } from "@src/@types/MinionPool";
import type { ProviderTypes } from "@src/@types/Providers";

/** The Swift Agents table (one row per Swift Agent pool). */
export const MINION_POOL_COLUMNS: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(220px, 2fr)" },
  { key: "endpoint", label: "Endpoint", width: "minmax(200px, 1.6fr)" },
  { key: "status", label: "Status", width: "124px" },
  { key: "machines", label: "Agent machines", width: "150px" },
  { key: "os", label: "OS", width: "84px" },
  { key: "created", label: "Created", width: "108px" },
];

const Bar = styled.div`
  width: 56px;
  height: 6px;
  border-radius: 9999px;
  background: ${T9.slate100};
  overflow: hidden;
  flex-shrink: 0;
  span {
    display: block;
    height: 100%;
    background: ${T9.brand500};
    border-radius: 9999px;
  }
`;

export const allocatedMachines = (pool: MinionPool) =>
  pool.minion_machines.filter(
    m => m.allocation_status === "IN_USE" || m.allocation_status === "AVAILABLE",
  ).length;

type Props = {
  item: MinionPool;
  onClick: () => void;
  selected: boolean;
  selectable?: boolean;
  endpointType: (endpointId: string) => ProviderTypes | string;
  endpointName?: (endpointId: string) => string | undefined;
  onSelectedChange: (value: boolean) => void;
  actions?: DropdownAction[];
};
@observer
class MinionPoolListItem extends React.Component<Props> {
  getStatus() {
    return this.props.item.status;
  }

  render() {
    const { item } = this.props;
    const endpointType = this.props.endpointType(item.endpoint_id);
    const endpointName = this.props.endpointName?.(item.endpoint_id) || "";
    const allocated = allocatedMachines(item);
    const pct = item.maximum_minions ? Math.min(100, Math.round((allocated / item.maximum_minions) * 100)) : 0;
    const machinesTitle = `${allocated} allocated · ${item.minimum_minions} minimum · ${item.maximum_minions} maximum`;
    return (
      <ListRow
        columns={MINION_POOL_COLUMNS}
        selectable={this.props.selectable}
        selected={this.props.selected}
        onSelectedChange={this.props.onSelectedChange}
        onClick={this.props.onClick}
        actions={this.props.actions}
        testId="minionPoolListItem"
      >
        <FlexCell gap={12}>
          <IconTile icon={Bot} size={28} />
          <Stack>
            <Primary title={item.name}>{item.name}</Primary>
            <Secondary title={item.notes || ""}>
              {item.platform === "source" ? "Source agents" : "Target agents"}
              {item.notes ? ` · ${item.notes}` : ""}
            </Secondary>
          </Stack>
        </FlexCell>
        <FlexCell gap={10} title={endpointName}>
          <ProviderBadge provider={endpointType} />
          <Cell>{endpointName || <Muted>—</Muted>}</Cell>
        </FlexCell>
        <div>
          <StatePill status={this.getStatus()} />
        </div>
        <FlexCell gap={10} title={machinesTitle}>
          <Bar>
            <span style={{ width: `${pct}%` }} />
          </Bar>
          <Stack>
            <Num align="left">
              {allocated} of {item.maximum_minions}
            </Num>
            <Secondary>min {item.minimum_minions}</Secondary>
          </Stack>
        </FlexCell>
        <div>
          <Pill tone="neutral">{item.os_type === "windows" ? "Windows" : "Linux"}</Pill>
        </div>
        <Cell title={formatDateTime(item.created_at)}>
          {formatShortDateTime(item.created_at)}
        </Cell>
      </ListRow>
    );
  }
}

export default MinionPoolListItem;
